import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import { useCallback } from 'react';
import { Stack } from 'src/core/ui/Stack';

import { Table as TableComponents } from './components/Table';
import { TablePagination } from './components/TablePagination';
import { useTableSetup } from './hooks/useTableSetup';
import {
  RowWrapper,
  RowWrapperV2,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

interface TableProps<T = any> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  useVirtuoso?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string;
  getItemId?: (data: T) => string | undefined;
  getRowId?: (data: T, index: number) => string; // Custom row ID generator
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For expandable rows
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  enableSubRowSelection?: boolean;
  onRowShiftSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  pagination?: TablePaginationConfig;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>;
  sortingFns?: Record<string, any>;
  meta?: Record<string, any>;
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  rowWrapperV2?: RowWrapperV2<T>;
  getSubRows?: (row: T) => T[]; // For expandable rows
  getRowCanExpand?: (row: { original: T }) => boolean; // For expandable rows
  manualPagination?: boolean;
  rowCount?: number;
  enableColumnFilters?: boolean;
}

// Internal simple table component
export const SimpleTable = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  variant = 'normal',
  alwaysShowCheckbox,
  getGroupId,
  getItemId,
  getRowId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  onFilteredRowsChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  onRowShiftSelect,
  pagination,
  className,
  onRowClick,
  summaryData,
  state,
  sortingFns,
  meta,
  footerCta,
  rowWrapper,
  rowWrapperV2,
  getSubRows,
  getRowCanExpand,
  manualPagination,
  rowCount,
  enableColumnFilters,
}: TableProps<T>) => {
  const { table, tableColumns, renderHeaderGroups, renderRowContent } =
    useTableSetup({
      actionColId,
      data,
      columns,
      alwaysShowCheckbox,
      getGroupId,
      getItemId,
      getRowId:
        getRowId ||
        (getItemId
          ? (data, index) => getItemId(data) ?? String(index)
          : undefined),
      isSelectionDisabled,
      onRowSelectionChange,
      onExpandedChange,
      onPaginationChange,
      onSortingChange,
      onFilteredRowsChange,
      enableRowSelection,
      enableMultiRowSelection,
      enableSubRowSelection,
      pagination,
      summaryData,
      state,
      sortingFns,
      meta,
      getSubRows,
      getRowCanExpand,
      manualPagination,
      rowCount,
      enableColumnFilters,
    });

  const getRowContent = useCallback(
    (row: Row<T>, index: number): React.ReactNode => {
      if (rowWrapperV2) {
        return rowWrapperV2(row, index, renderRowContent);
      }
      const cellContent = renderRowContent(row);
      return rowWrapper
        ? rowWrapper(row.original, cellContent, index)
        : cellContent;
    },
    [renderRowContent, rowWrapper, rowWrapperV2]
  );

  return (
    <>
      <TableComponents.Root
        table={table}
        variant={variant}
        className={className}
      >
        <TableComponents.Header>
          <>{renderHeaderGroups()}</>
        </TableComponents.Header>

        <TableComponents.Body>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row, index) => {
              const handleClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                row.toggleSelected();
                onRowShiftSelect?.(
                  table.getRowModel().rows as Row<T | null>[],
                  row as Row<T | null>,
                  index
                );
              };

              return (
                <TableComponents.Row
                  key={row.id}
                  onClick={handleClick}
                  selected={row.getIsSelected()}
                  selectable={Boolean(handleClick)}
                >
                  {getRowContent(row, index)}
                </TableComponents.Row>
              );
            })
          ) : (
            <TableComponents.Row>
              <TableComponents.Cell
                colSpan={tableColumns.length}
                align="center"
              >
                No data available
              </TableComponents.Cell>
            </TableComponents.Row>
          )}
        </TableComponents.Body>
        <TableComponents.Footer />
      </TableComponents.Root>
      <Stack justifyContent="spaceBetween">
        {pagination && (
          <TablePagination
            table={table}
            pageSize={pagination.pageSize}
            variant={pagination.variant}
            totalCount={pagination.totalCount}
          />
        )}
        {footerCta}
      </Stack>
    </>
  );
};
